import React, { useRef, useState } from "react";
import { DownloadIcon } from "../../admin/components/common/Icons";
import "../../assets/adminSupportTickets.css";

interface Props {
  onSubmit: (payload: { message: string; files?: File[] }) => Promise<void>;
  loading?: boolean;
  placeholder?: string;
}

const TicketReplyForm: React.FC<Props> = ({
  onSubmit,
  loading = false,
  placeholder = "Write your reply...",
}) => {
  const [message, setMessage] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = e.target.files ? Array.from(e.target.files) : [];
    setFiles((prev) => [...prev, ...selectedFiles]);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const downloadFile = (file: File) => {
    const url = URL.createObjectURL(file);
    const link = document.createElement("a");

    link.href = url;
    link.download = file.name;
    link.click();

    URL.revokeObjectURL(url);
  };

  const isImageFile = (file: File) => {
    return file.type.startsWith("image/");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!message.trim()) return;

    await onSubmit({ message, files });
    setMessage("");
    setFiles([]);
  };

  return (
    <form className="ticket-reply-form" onSubmit={handleSubmit}>
      <textarea
        rows={5}
        placeholder={placeholder}
        value={message}
        onChange={(e) => setMessage(e.target.value)}
      />

      <div className="ticket-reply-upload-area">
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="ticket-reply-hidden-file"
          onChange={handleFileChange}
          accept=".jpg,.gif,.jpeg,.png,.doc,.xls,.pdf,.txt,.docx,.xlsx,.rar,.zip"
        />

        <button
          type="button"
          className="ticket-reply-upload-btn"
          onClick={() => fileInputRef.current?.click()}
        >
          Upload File
        </button>

        {files.length > 0 && (
          <div className="ticket-reply-file-preview-wrap">
            {files.map((file, index) => {
              const previewUrl = isImageFile(file)
                ? URL.createObjectURL(file)
                : "";

              return (
                <div
                  className="ticket-reply-file-preview"
                  key={`${file.name}-${index}`}
                >
                  {isImageFile(file) ? (
                    <img src={previewUrl} alt={file.name} />
                  ) : (
                    <div className="ticket-reply-file-icon">
                      <i className="fa fa-file-text-o"></i>
                      <span>{file.name}</span>
                    </div>
                  )}

                  <button
                    type="button"
                    className="ticket-reply-file-remove"
                    onClick={() => removeFile(index)}
                    title="Remove"
                  >
                    ×
                  </button>

                  <button
                    type="button"
                    className="ticket-reply-file-download"
                    onClick={() => downloadFile(file)}
                    title="Download"
                  >
                    <DownloadIcon />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <button
        type="submit"
        className="ticket-reply-submit-btn"
        disabled={loading}
      >
        {loading ? "Sending..." : "Send Reply"}
      </button>
    </form>
  );
};

export default TicketReplyForm;
